import React from 'react';
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const loadingVariants = cva(
  "inline-flex items-center justify-center",
  {
    variants: {
      variant: {
        spinner: "animate-spin rounded-full border-solid border-current border-r-transparent",
        dots: "space-x-1",
        pulse: "animate-pulse",
        skeleton: "animate-pulse bg-muted rounded",
      },
      size: {
        sm: "h-4 w-4",
        default: "h-8 w-8",
        lg: "h-12 w-12",
        xl: "h-16 w-16",
      },
    },
    defaultVariants: {
      variant: "spinner",
      size: "default",
    },
  }
);

export interface LoadingProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof loadingVariants> {
  text?: string;
  fullScreen?: boolean;
}

const Loading = React.forwardRef<HTMLDivElement, LoadingProps>(
  ({ className, variant, size, text, fullScreen = false, ...props }, ref) => {
    const LoadingElement = () => {
      switch (variant) {
        case "dots":
          return (
            <div className={cn(loadingVariants({ variant, className }))}>
              <div className="h-2 w-2 bg-current rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="h-2 w-2 bg-current rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="h-2 w-2 bg-current rounded-full animate-bounce"></div>
            </div>
          );
        case "pulse":
          return (
            <div className={cn(loadingVariants({ variant, size, className }))}>
              <div className="h-full w-full bg-current rounded-full"></div>
            </div>
          );
        case "skeleton":
          return (
            <div className={cn(loadingVariants({ variant, className }))} style={{ height: size === 'sm' ? '1rem' : size === 'lg' ? '3rem' : size === 'xl' ? '4rem' : '2rem' }}>
            </div>
          );
        default: // spinner
          return (
            <div className={cn(loadingVariants({ variant, size, className }), "border-2")}>
            </div>
          );
      }
    };

    const content = (
      <div className={cn("flex flex-col items-center justify-center gap-2", text && "space-y-2")}>
        <LoadingElement />
        {text && (
          <span className="text-sm text-muted-foreground" aria-live="polite">
            {text}
          </span>
        )}
      </div>
    );

    if (fullScreen) {
      return (
        <div
          ref={ref}
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center"
          role="status"
          aria-label={text || "Loading"}
          {...props}
        >
          {content}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn("flex items-center justify-center", className)}
        role="status"
        aria-label={text || "Loading"}
        {...props}
      >
        {content}
      </div>
    );
  }
);

Loading.displayName = "Loading";

// Specialized loading components for common use cases
export const ButtonLoading: React.FC<{ className?: string }> = ({ className }) => (
  <Loading variant="spinner" size="sm" className={cn("text-current", className)} />
);

export const PageLoading: React.FC<{ text?: string }> = ({ text = "Loading..." }) => (
  <Loading variant="spinner" size="lg" text={text} className="py-12" />
);

export const FullScreenLoading: React.FC<{ text?: string }> = ({ text = "Loading..." }) => (
  <Loading variant="spinner" size="xl" text={text} fullScreen />
);

export const CardSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn("space-y-3", className)}>
    <Loading variant="skeleton" className="h-4 w-3/4" />
    <Loading variant="skeleton" className="h-4 w-1/2" />
    <Loading variant="skeleton" className="h-20 w-full" />
  </div>
);

export const ListSkeleton: React.FC<{ items?: number; className?: string }> = ({ 
  items = 3, 
  className 
}) => (
  <div className={cn("space-y-2", className)}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center space-x-3">
        <Loading variant="skeleton" className="h-8 w-8 rounded-full" />
        <div className="space-y-1 flex-1">
          <Loading variant="skeleton" className="h-4 w-3/4" />
          <Loading variant="skeleton" className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

export { Loading, loadingVariants };